



























































































































































































































































































import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  Consignment,
  ConsignmentFilters,
  TransactionType,
  Account,
  Depot,
  ConsignmentStatus,
  JobType, AMSGroup,
} from '@/store';
import moment from 'moment-timezone';
import _ from 'lodash';
import { formatDatetime } from '@/main';
import FilterSummary from '@/components/FilterSummary.vue';
import SvgIcon from '@jamescoyle/vue-icon';
import { mdiNoteEditOutline } from '@mdi/js';
import { LMap, LMarker, LWMSTileLayer } from 'vue2-leaflet';

interface SimpleColumn {
  field: keyof Consignment;
  title: string;
  filter?: 'date' | 'transaction' | 'time';
}

interface DatetimeColumn {
  field: [keyof Consignment, keyof Consignment];
  title: string;
  filter?: 'datetime';
}

interface AddressColumn {
  field: [keyof Consignment, keyof Consignment];
  title: string;
  filter?: 'address';
}

interface ConcatColumn {
  field: [keyof Consignment, keyof Consignment];
  title: string;
  filter: 'concat';
}

type Column = SimpleColumn | DatetimeColumn | AddressColumn | ConcatColumn;

const COLUMNS: Column[] = [
  {
    field: 'tracking_id',
    title: 'Tracking ID',
  },
  {
    field: 'consignment_number',
    title: 'Consignment No.',
  },
  {
    field: 'transaction',
    title: 'Type',
    filter: 'transaction',
  },
  {
    field: 'account_name',
    title: 'Account Name',
  },
  {
    field: 'date_entered',
    title: 'Entered',
    filter: 'date',
  },
  {
    field: ['due_date', 'due_time'],
    title: 'Due',
    filter: 'datetime',
  },
  {
    field: ['original_due_date', 'original_due_time'],
    title: 'Orignally Due',
    filter: 'datetime',
  },
  {
    // field: ['delivery_address_contact', 'delivery_address_company'],
    field: 'delivery_address_company',
    title: 'Customer Name',
    // filter: 'concat',
  },
  {
    field: ['delivery_address_postcode', 'delivery_address_country'],
    title: 'Destination',
    filter: 'address',
  },
  {
    field: 'delivery_depot_number',
    title: 'Del. Depot',
  },
  {
    field: 'consignment_status',
    title: 'Status',
  },
  {
    field: 'eta',
    title: 'ETA',
  },
];

export default Vue.extend({
  name: 'Home',
  components: { FilterSummary, SvgIcon, LMap, LMarker, 'l-wms-tile-layer': LWMSTileLayer },
  props: {
    jobType: String as () => JobType,
  },
  data() {
    return {
      columns: COLUMNS,
      search: '',
      jobFilters: {
        status: [],
        transaction: [],
        account: [],
        hmDepot: [],
        deliveryDepot: [],
        enteredFrom: undefined,
        enteredTo: undefined,
        dueFrom: moment(),
        dueTo: moment(),
        amsGroup: [],
      } as ConsignmentFilters,
      accountDropdownFilter: '',
      deliveryDepotDropdownFilter: '',
      ordering: ['due_date', 'due_time'],
      updateTimeout: undefined as number | undefined,
      ukCountries: ['ENGLAND', 'WALES', 'SCOTLAND', 'NORTHERN IRELAND'],
      generatingSpreadsheet: false,
      newReviewNote: '',
      mdiNoteEditOutline,
      osm: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    };
  },
  computed: {
    ...mapGetters(['showExperimentalFeatures']),
    nonHMDepotLabel(): string {
      return this.jobType === 'Network' || this.jobType === 'Network Collection' ? 'Paying Depot' : 'Delivery Depot';
    },
    filterEnteredFrom: {
      get(): string | undefined {
        return this.jobFilters.enteredFrom?.format('YYYY-MM-DD');
      },
      set(value: string) {
        this.jobFilters.enteredFrom = moment(value, 'YYYY-MM-DD');
      },
    },
    filterEnteredTo: {
      get(): string | undefined {
        return this.jobFilters.enteredTo?.format('YYYY-MM-DD');
      },
      set(value: string) {
        this.jobFilters.enteredTo = moment(value, 'YYYY-MM-DD');
      },
    },
    filterDueFrom: {
      get(): string | undefined {
        return this.jobFilters.dueFrom?.format('YYYY-MM-DD');
      },
      set(value: string) {
        this.jobFilters.dueFrom = moment(value, 'YYYY-MM-DD');
      },
    },
    filterDueTo: {
      get(): string | undefined {
        return this.jobFilters.dueTo?.format('YYYY-MM-DD');
      },
      set(value: string | null) {
        this.jobFilters.dueTo = moment(value, 'YYYY-MM-DD');
      },
    },
    filteredAccounts(): Account[] {
      const searchString = this.accountDropdownFilter.toLowerCase();
      return this.accounts.filter((a: Account) => a.account_name.toLowerCase().includes(searchString));
    },
    filteredDepots(): Depot[] {
      const searchString = this.deliveryDepotDropdownFilter.toLowerCase();
      return this.depots.filter((d: Depot) => d.depot_number.toString().includes(searchString) ||
        d.depot_name.toLowerCase().includes(searchString));
    },
    accountNameMap(): { [k: string]: string } {
      return this.accounts.reduce((accts: { [k: string]: string }, a: Account) => {
        accts[a.account_code] = a.account_name;
        return accts;
      }, {});
    },
    amsGroupMap(): { [k: number]: string } {
      return this.amsGroups.reduce((groups: { [k: number]: string }, a: AMSGroup) => {
        groups[a.id] = a.name;
        return groups;
      }, {});
    },
    statusNameMap(): { [k: string]: string } {
      return this.statuses.reduce((statuses: { [k: string]: string }, s: ConsignmentStatus) => {
        statuses[s.consignment_status_id] = s.consignment_status;
        return statuses;
      }, {});
    },
    ...mapState('consignments', {
      loading: 'loading',
    }),
    ...mapGetters({
      consignments: 'consignments/currentPage',
      showPrev: 'consignments/hasPrev',
      showNext: 'consignments/hasNext',
      total: 'consignments/total',
      statuses: 'consignmentStatuses/all',
      accounts: 'accounts/all',
      depots: 'depots/all',
      incidentTypes: 'incidentTypes/all',
      amsGroups: 'amsGroups/all',
    }),
  },
  filters: {
    transaction(tx: TransactionType): string {
      switch (tx) {
      case 'D':
        return 'DEL';
      case 'C':
        return 'COL';
      case '3':
        return '3RD';
      }
    },
  },
  methods: {
    statusClass(c: Consignment): string {
      if (c.consignment_status === '900 - JOB COMPLETE') {
        return '';
      }
      const hoursLeft = moment(`${c.due_date} ${c.due_time}`).diff(moment(), 'hours', true);
      if (hoursLeft < 0) {
        return 'late';
      } else if (hoursLeft < 1) {
        return 'nearly-late';
      } else if (moment(`${c.original_due_date} ${c.original_due_time}`).isBefore(moment.now())) {
        return 'past-original-due-date';
      } else {
        return '';
      }
    },
    consignmentRowClasses(c: Consignment): {[className: string]: boolean} {
      const classes: Record<string, boolean> = { 'at-risk': c.at_risk };
      const statusClass = this.statusClass(c);
      if (statusClass !== '') {
        classes[statusClass] = true;
      }
      return classes;
    },
    async update() {
      if (this.updateTimeout) {
        window.clearTimeout(this.updateTimeout);
      }
      await this.$store.dispatch('consignments/load', {
        jobType: this.jobType,
        generic: this.search,
        ordering: this.ordering,
        filters: this.jobFilters,
      });
      this.updateTimeout = window.setTimeout(this.update, 5 * 60 * 1000);
    },
    async setOrdering(key: string | string[]) {
      if (typeof key === 'string') {
        key = [key];
      }
      const descKey = key.map((k) => `-${k}`);
      if (_.isEqual(this.ordering, key)) {
        this.ordering = descKey;
      } else {
        this.ordering = key;
      }
      await this.update();
    },
    orderingClass(key: string | string[]): { sorted: boolean; desc: boolean } {
      if (typeof key === 'string') {
        key = [key];
      }
      const descKey = key.map((k) => `-${k}`);
      return {
        sorted: _.isEqual(this.ordering, key) || _.isEqual(this.ordering, descKey),
        desc: _.isEqual(this.ordering, descKey),
      };
    },
    selectAll<T, K extends keyof T>(target: T[K][], options: T[], key: K) {
      if (target.length === options.length) {
        target.splice(0, target.length);
      } else {
        target.splice(0, target.length, ...options.map((o) => o[key]));
      }
    },
    markReviewed(c: Consignment) {
      if (c.reviewed) {
        this.$store.dispatch('consignments/markUnreviewed', c);
      } else {
        this.$store.dispatch('consignments/markReviewed', c);
      }
    },
    toggleExcluded(c: Consignment) {
      if (c.excluded_from_review) {
        this.$store.dispatch('consignments/includeInReview', c);
      } else {
        this.$store.dispatch('consignments/excludeFromReview', c);
      }
    },
    incidentTooltip(c: Consignment) {
      if (c.incident) {
        return `Recorded by ${c.incident.by_name} at ${formatDatetime(c.incident.at)}`;
      } else {
        return '';
      }
    },
    reviewTooltip(c: Consignment) {
      if (c.reviewed) {
        return `Reviewed by ${c.reviewed_by} at ${formatDatetime(c.reviewed_at)}`;
      } else {
        return '';
      }
    },
    openReviewNote(c: Consignment) {
      this.newReviewNote = c.review_note;
      (this.$refs[`review-note-dialog-${c.tracking_id}`] as HTMLDialogElement[])[0].showModal();
    },
    async saveReviewNote(c: Consignment) {
      await this.$store.dispatch('consignments/setReviewNote', { consignment: c, note: this.newReviewNote });
      this.newReviewNote = '';
    },
    ...mapActions({
      prev: 'consignments/prev',
      next: 'consignments/next',
    }),
    async changeIncidentType(consignment: Consignment, incidentTypeId: number|'') {
      if (incidentTypeId === '') {
        this.$store.dispatch('consignments/deleteIncident', consignment);
      } else {
        this.$store.dispatch('consignments/recordIncident', { consignment, incidentTypeId });
      }
    },
    async downloadSpreadsheet() {
      this.generatingSpreadsheet = true;
      const response: Blob = await this.$store.dispatch('consignments/exportToExcel', {
        generic: this.search,
        ordering: this.ordering,
        filters: this.jobFilters,
      });
      const url = await URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'consignments.xlsx';
      const body = document.querySelector('body');
      if (!body) {
        // Can't happen
        return;
      }
      body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      this.generatingSpreadsheet = false;
    },
    initials(name: string): string {
      return name.split(/ /).filter((s) => s.length > 0).map((s) => s[0].toUpperCase()).join('');
    },
  },
  watch: {
    async search() {
      await this.update();
    },
    jobFilters: {
      deep: true,
      async handler() {
        await this.update();
      },
    },
    jobType: {
      async handler() {
        await this.update();
      },
    },
  },
  created() {
    this.$store.dispatch('consignmentStatuses/load');
    this.$store.dispatch('accounts/load');
    this.$store.dispatch('depots/load');
    this.$store.dispatch('incidentTypes/load');
    this.$store.dispatch('amsGroups/load');
    this.update();
  },
});
